import { ScheduledCourse, GroupedCourse, Preferences, CourseList } from '../types/course';
import { SCHEDULE_DISPLAY_LIMITS, TRAVEL_TIMES } from '../lib/constants';
import { AVAILABILITY_DAYS, AVAILABILITY_SLOT_COUNT, getAvailabilitySlotIndex } from '../lib/availabilityGrid';
import { openSectionsService } from './api';
import type { GeneratedSchedule, NoScheduleReason } from '../types/interfaces';

const DEBUG_SCHEDULE_GEN = process.env.NEXT_PUBLIC_DEBUG_SCHEDULES === 'true';

// Treat fully online/asynchronous courses as not impacting time preference
function isOnlineAsync(
  item: { campus?: string | null; meetingTimes: Array<{ mode?: string | undefined }> }
): boolean {
  const campus = item.campus || '';
  const campusRemote = /remote|online|web|virtual|asynchronous/i.test(campus);
  const mtRemote = item.meetingTimes?.some(mt => /remote|online|web|virtual|asynchronous/i.test(mt?.mode || '')) ?? false;
  return campusRemote || mtRemote;
}

// Check if a course has TBA (To Be Announced) meeting times
// TBA classes have invalid or zero startMinute/endMinute values, or day === 'TBA'
function isTBA(
  item: { meetingTimes: Array<{ day?: string; startMinute?: number; endMinute?: number }> }
): boolean {
  if (!item.meetingTimes || item.meetingTimes.length === 0) return true;
  return item.meetingTimes.every(mt => {
    const day = mt.day || '';
    const startMin = mt.startMinute ?? 0;
    const endMin = mt.endMinute ?? 0;
    // TBA if day is 'TBA' or if start/end times are invalid (0 or negative)
    return day.toUpperCase() === 'TBA' || startMin <= 0 || endMin <= 0;
  });
}

// Normalize campus labels from data to preference keys
function normalizeCampusName(raw: string | null | undefined): string {
  if (!raw) return '';
  const v = String(raw).toLowerCase().trim();
  if (/remote|online|web|virtual|asynchronous/.test(v)) return 'online';
  if (/^cac$|college\s*ave(nue)?/.test(v)) return 'college ave';
  if (/livingston|^livi$/.test(v)) return 'livi';
  if (/busch/.test(v)) return 'busch';
  if (/cook|douglass|cook\s*\/\s*douglass|cook\s*douglass|cook\/doug/.test(v)) return 'cook/doug';
  return v;
}

// --- Conflict Checking ---
function* conflictBetweenSteps(s1: ScheduledCourse, s2: ScheduledCourse): Steps<boolean> {
  // TBA classes don't conflict with anything
  if (isTBA(s1) || isTBA(s2)) return false;

  for (const mt1 of s1.meetingTimes) {
    for (const mt2 of s2.meetingTimes) {
      yield;
      // Skip invalid meeting times (TBA)
      if (!mt1.startMinute || !mt1.endMinute || !mt2.startMinute || !mt2.endMinute ||
          mt1.startMinute <= 0 || mt1.endMinute <= 0 || mt2.startMinute <= 0 || mt2.endMinute <= 0) {
        continue;
      }
      if (mt1.day === mt2.day) {
        // Direct time overlap
        if (mt1.startMinute < mt2.endMinute && mt2.startMinute < mt1.endMinute) {
          return true;
        }
        // Check for travel time between different campuses
        if (s1.campus !== s2.campus) {
          const travelTime = TRAVEL_TIMES[s1.campus]?.[s2.campus] || 30; // Default to 30 if not found
          if (mt1.endMinute <= mt2.startMinute && (mt2.startMinute - mt1.endMinute) < travelTime) {
            return true; // Not enough time to travel from s1 to s2
          }
          if (mt2.endMinute <= mt1.startMinute && (mt1.startMinute - mt2.endMinute) < travelTime) {
            return true; // Not enough time to travel from s2 to s1
          }
        }
      }
    }
  }
  return false;
}

type Steps<T> = Generator<void, T, void>;
type OpenSectionLookup = (index: string) => boolean;

export interface CooperativeGenerationInput {
  groupedCourses: GroupedCourse[];
  courseLists: CourseList[];
  pinnedClasses: string[];
  generateOnlyOpenSections: boolean;
  preferences?: Preferences | null;
  openSectionsTermId: string;
  friendSchedulesForScoring?: ScheduledCourse[][];
}

export interface CooperativeGenerationOptions {
  signal?: AbortSignal;
  /** Absolute Date.now() deadline, including task yields and all computation phases. */
  deadlineMs?: number;
  yieldTask?: () => Promise<void>;
  maxStepsPerSlice?: number;
  maxSliceMs?: number;
}

export interface CooperativeGenerationResult {
  schedules: GeneratedSchedule[];
  noScheduleReasons: NoScheduleReason[];
}

export class ScheduleGenerationTimeoutError extends Error {
  constructor() {
    super('Schedule generation timed out.');
    this.name = 'TimeoutError';
  }
}

function drain<T>(steps: Steps<T>): T {
  let next = steps.next();
  while (!next.done) next = steps.next();
  return next.value;
}

/** Stable merge order matches Array.sort, with checkpoints even during large section sorts. */
function* sortSteps<T>(items: readonly T[], compare: (a: T, b: T) => number): Steps<T[]> {
  let source = [...items];
  for (let width = 1; width < source.length; width *= 2) {
    const target = new Array<T>(source.length);
    for (let start = 0; start < source.length; start += width * 2) {
      const middle = Math.min(start + width, source.length);
      const end = Math.min(start + width * 2, source.length);
      let left = start;
      let right = middle;
      for (let index = start; index < end; index++) {
        yield;
        // A NaN comparison, like Array.sort's comparator, is an equal comparison.
        const takeLeft = left < middle && (right >= end || !(compare(source[left], source[right]) > 0));
        target[index] = takeLeft ? source[left++] : source[right++];
      }
    }
    source = target;
  }
  return source;
}

function estimateSectionScore(section: ScheduledCourse, preferences?: Preferences | null): number {
  if (!preferences) return 0; // no ordering if prefs absent

  const weights = new Map<string, number>();
  const totalPriority = (preferences.priorities || []).reduce((s, p) => s + (p.value || 0), 0) || 100;
  (preferences.priorities || [
    { name: 'campus', value: 33, color: '' },
    { name: 'professor ratings', value: 33, color: '' },
    { name: 'times', value: 34, color: '' }
  ]).forEach(p => weights.set(p.name.toLowerCase(), (p.value || 0) / totalPriority));

  // Professor
  let professorScore = 0;
  const ratings = section.instructors.map(i => (typeof i !== 'string' && i.rating) ? i.rating : 3.0);
  if (ratings.length > 0) {
    const avg = ratings.reduce((a, b) => a + b, 0) / ratings.length;
    professorScore = (avg / 5) * 100;
  }

  // Time: do not penalize online/asynchronous or TBA courses at all
  let timePenalty = 0;
  const online = isOnlineAsync(section);
  const tba = isTBA(section);
  if (!online && !tba) {
    for (const mt of section.meetingTimes) {
      // Skip invalid meeting times (TBA)
      if (!mt.startMinute || !mt.endMinute || mt.startMinute <= 0 || mt.endMinute <= 0) continue;

      const dayIndex = AVAILABILITY_DAYS.indexOf(mt.day as typeof AVAILABILITY_DAYS[number]);
      if (dayIndex !== -1) {
        for (let m = mt.startMinute; m < mt.endMinute; m += 60) {
          const hourIdx = getAvailabilitySlotIndex(m);
          if (hourIdx >= 0 && hourIdx < AVAILABILITY_SLOT_COUNT) {
            const unavailable = !preferences.availability?.[dayIndex]?.[hourIdx];
            if (unavailable) timePenalty += 1;
          }
        }
      }
      if (mt.startMinute < preferences.earliestStartTime) timePenalty += 4;
      if (mt.endMinute > preferences.latestEndTime) timePenalty += 4;
    }
  }
  const timeScore = Math.max(0, 100 - Math.min(50, timePenalty));

  // Campus (use normalized labels and same computation as schedule-level campus score)
  const campusScore = computeCampusScore([section], preferences);

  const campusW = weights.get('campus') ?? 0.34;
  const profW = weights.get('professor ratings') ?? 0.33;
  const timeW = weights.get('times') ?? 0.33;
  return campusScore * campusW + professorScore * profW + timeScore * timeW;
}

function* rankSectionsSteps(sections: ScheduledCourse[], preferences?: Preferences | null): Steps<ScheduledCourse[]> {
  if (!preferences) return sections;
  const scored: Array<{ section: ScheduledCourse; score: number }> = [];
  for (const section of sections) {
    yield;
    scored.push({ section, score: estimateSectionScore(section, preferences) });
  }
  const ranked = yield* sortSteps(scored, (a, b) => b.score - a.score);
  return ranked.map(item => item.section);
}

interface PreparedGroup {
  group: GroupedCourse;
  sections: ScheduledCourse[];
  listCourses: ScheduledCourse[][];
  beforeOpenCount: number;
}

/** Explicit configuration and pinned choices retain their intentional closed overrides. */
function* prepareGroupsSteps(input: CooperativeGenerationInput, isOpen: OpenSectionLookup, rank: boolean): Steps<PreparedGroup[]> {
  const prepared: PreparedGroup[] = [];
  for (const group of input.groupedCourses) {
    yield;
    if (group.isCourseList && group.courseList) {
      const byCourse = new Map<string, ScheduledCourse[]>();
      for (const section of group.sections || []) {
        yield;
        const sections = byCourse.get(section.baseId) || [];
        sections.push(section);
        byCourse.set(section.baseId, sections);
      }
      const listCourses: ScheduledCourse[][] = [];
      // Preserve the original cap before allowed/open filtering.
      const capped = Array.from(byCourse.values()).slice(0, SCHEDULE_DISPLAY_LIMITS.MAX_DISTINCT_COURSE_LIST_COURSES);
      for (const sections of capped) {
        const allowed = group.allowedCourses?.[sections[0].baseId];
        const allowedSet = Array.isArray(allowed) ? new Set(allowed) : null;
        let eligible: ScheduledCourse[] = [];
        for (const section of sections) {
          yield;
          if (allowedSet && !allowedSet.has(section.id)) continue;
          if (input.generateOnlyOpenSections && !allowedSet?.has(section.id)) {
            const index = section.id.split(':').pop();
            if (!index || !isOpen(index)) continue;
          }
          eligible.push(section);
        }
        if (eligible.length) {
          if (rank) eligible = yield* rankSectionsSteps(eligible, input.preferences);
          listCourses.push(eligible);
        }
      }
      prepared.push({ group, sections: [], listCourses, beforeOpenCount: 0 });
      continue;
    }
    const pinned = input.pinnedClasses.includes(group.id);
    const allowed = group.allowedSections?.length ? new Set(group.allowedSections) : null;
    const source = allowed ? (group.sections || []) : pinned ? [group.selectedSection].filter(Boolean) as ScheduledCourse[] : (group.sections || []);
    let sections: ScheduledCourse[] = [];
    let beforeOpenCount = 0;
    for (const section of source) {
      yield;
      if (allowed && !allowed.has(section.id)) continue;
      beforeOpenCount++;
      if (input.generateOnlyOpenSections) {
        const index = section.id.split(':').pop();
        if (!index || (!allowed?.has(section.id) && !pinned && !isOpen(index))) continue;
      }
      sections.push(section);
    }
    if (rank) sections = yield* rankSectionsSteps(sections, input.preferences);
    prepared.push({ group, sections, listCourses: [], beforeOpenCount });
  }
  return prepared;
}

function* enumerateSteps(prepared: PreparedGroup[]): Steps<ScheduledCourse[][]> {
  const schedules: ScheduledCourse[][] = [];
  const maximum = 1000;
  function* findCombinations(courseIndex: number, current: ScheduledCourse[]): Steps<void> {
    yield;
    if (schedules.length >= maximum) return;
    if (courseIndex >= prepared.length) {
      schedules.push([...current]);
      return;
    }
    const item = prepared[courseIndex];
    if (item.group.isCourseList && item.group.courseList) {
      const quantity = item.group.quantity || 1;
      if (quantity <= 0 || item.listCourses.length < quantity) return;
      function* chooseSections(index: number, chosen: ScheduledCourse[], picked: ScheduledCourse[][]): Steps<void> {
        yield;
        if (schedules.length >= maximum) return;
        if (index === picked.length) {
          current.push(...chosen);
          yield* findCombinations(courseIndex + 1, current);
          for (let count = 0; count < chosen.length; count++) current.pop();
          return;
        }
        for (const section of picked[index]) {
          yield;
          let conflict = false;
          for (const other of current) {
            if (yield* conflictBetweenSteps(section, other)) { conflict = true; break; }
          }
          if (!conflict) {
            for (const other of chosen) {
              if (yield* conflictBetweenSteps(section, other)) { conflict = true; break; }
            }
          }
          if (!conflict) {
            chosen.push(section);
            yield* chooseSections(index + 1, chosen, picked);
            chosen.pop();
          }
          if (schedules.length >= maximum) return;
        }
      }
      function* chooseCourseCombos(start: number, picked: ScheduledCourse[][]): Steps<void> {
        yield;
        if (schedules.length >= maximum) return;
        if (picked.length === quantity) {
          yield* chooseSections(0, [], picked);
          return;
        }
        for (let index = start; index < item.listCourses.length; index++) {
          yield;
          picked.push(item.listCourses[index]);
          yield* chooseCourseCombos(index + 1, picked);
          picked.pop();
          if (schedules.length >= maximum) return;
        }
      }
      yield* chooseCourseCombos(0, []);
      return;
    }
    for (const section of item.sections) {
      yield;
      let conflict = false;
      for (const other of current) {
        if (yield* conflictBetweenSteps(section, other)) { conflict = true; break; }
      }
      if (!conflict) {
        current.push(section);
        yield* findCombinations(courseIndex + 1, current);
        current.pop();
      }
      if (schedules.length >= maximum) return;
    }
  }
  yield* findCombinations(0, []);
  return schedules;
}

function* diagnoseSteps(prepared: PreparedGroup[], input: CooperativeGenerationInput): Steps<NoScheduleReason[]> {
  const reasons: NoScheduleReason[] = [];
  // Preferences rank sections; they never remove candidates from the search.
  for (const item of prepared) {
    yield;
    const { group } = item;
    if (group.isCourseList && group.courseList) {
      const required = group.quantity || 1;
      if (item.listCourses.length < required) reasons.push({
        type: 'list_quantity_insufficient',
        message: `Course list "${group.name}" requires ${required} course(s) but only ${item.listCourses.length} eligible course(s) are available within the search limit.`,
        listId: group.courseList.id,
      });
    } else if (!group.isCourseList && input.generateOnlyOpenSections && item.beforeOpenCount > 0 && item.sections.length === 0) {
      reasons.push({ type: 'all_sections_closed', message: `All eligible sections are closed for ${group.name}.`, courseIds: [group.id] });
    }
  }
  const pinned: ScheduledCourse[] = [];
  for (const item of prepared) {
    yield;
    const { group, sections } = item;
    if (!group.isCourseList && input.pinnedClasses.includes(group.id) && group.selectedSection && sections.length === 1 && sections[0].id === group.selectedSection.id) pinned.push(group.selectedSection);
  }
  for (let a = 0; a < pinned.length; a++) {
    for (let b = a + 1; b < pinned.length; b++) {
      yield;
      if (yield* conflictBetweenSteps(pinned[a], pinned[b])) reasons.push({
        type: 'pinned_conflict', message: `Pinned classes ${pinned[a].name} and ${pinned[b].name} conflict.`, involved: [pinned[a].id, pinned[b].id],
      });
    }
  }
  const regular = prepared.filter(item => !item.group.isCourseList);
  for (let a = 0; a < regular.length; a++) {
    for (let b = a + 1; b < regular.length; b++) {
      yield;
      if (!regular[a].sections.length || !regular[b].sections.length) continue;
      let compatible = false;
      outer: for (const first of regular[a].sections) {
        for (const second of regular[b].sections) {
          yield;
          if (!(yield* conflictBetweenSteps(first, second))) { compatible = true; break outer; }
        }
      }
      if (!compatible) reasons.push({
        type: 'pairwise_conflict', message: `All sections of ${regular[a].group.name} conflict with all sections of ${regular[b].group.name}.`, courseIds: [regular[a].group.id, regular[b].group.id],
      });
    }
  }
  return reasons;
}

function legacyInput(groupedCourses: GroupedCourse[], courseLists: CourseList[], pinnedClasses: string[], generateOnlyOpenSections: boolean, preferences: Preferences | null | undefined, openSectionsTermId: string): CooperativeGenerationInput {
  return { groupedCourses, courseLists, pinnedClasses, generateOnlyOpenSections, preferences, openSectionsTermId };
}

/** Synchronous tool callers retain the same API, order, caps and eligibility semantics. */
export function generateSchedules(groupedCourses: GroupedCourse[], courseLists: CourseList[], pinnedClasses: string[], generateOnlyOpenSections = false, preferences?: Preferences | null, openSectionsTermId = 'fall-2026'): ScheduledCourse[][] {
  const input = legacyInput(groupedCourses, courseLists, pinnedClasses, generateOnlyOpenSections, preferences, openSectionsTermId);
  if (DEBUG_SCHEDULE_GEN) { console.groupCollapsed('ScheduleGen: start'); console.time('ScheduleGen: total'); }
  const prepared = drain(prepareGroupsSteps(input, index => openSectionsService.isOpen(index, openSectionsTermId), true));
  const schedules = drain(enumerateSteps(prepared));
  if (DEBUG_SCHEDULE_GEN) { console.log('generated schedules:', schedules.length, schedules.length >= 1000 ? '(truncated)' : ''); console.timeEnd('ScheduleGen: total'); console.groupEnd(); }
  return schedules;
}

export function diagnoseNoSchedules(groupedCourses: GroupedCourse[], courseLists: CourseList[], pinnedClasses: string[], generateOnlyOpenSections = false, preferences?: Preferences | null, openSectionsTermId = 'fall-2026'): NoScheduleReason[] {
  const input = legacyInput(groupedCourses, courseLists, pinnedClasses, generateOnlyOpenSections, preferences, openSectionsTermId);
  const prepared = drain(prepareGroupsSteps(input, index => openSectionsService.isOpen(index, openSectionsTermId), false));
  return drain(diagnoseSteps(prepared, input));
}

function* optionsSteps(input: CooperativeGenerationInput, isOpen: OpenSectionLookup): Steps<CooperativeGenerationResult> {
  const prepared = yield* prepareGroupsSteps(input, isOpen, true);
  const candidates = yield* enumerateSteps(prepared);
  const scored: GeneratedSchedule[] = [];
  for (const courses of candidates) {
    yield;
    scored.push({ courses, score: input.preferences ? scoreSchedule(courses, input.preferences, input.friendSchedulesForScoring) : 0 });
  }
  const schedules = yield* sortSteps(scored, (a, b) => b.score - a.score);
  const noScheduleReasons = schedules.length ? [] : (yield* diagnoseSteps(prepared, input));
  return { schedules, noScheduleReasons };
}

function checkRun(options: CooperativeGenerationOptions): void {
  if (options.signal?.aborted) {
    const error = new Error('Schedule generation cancelled.');
    error.name = 'AbortError';
    throw error;
  }
  if (options.deadlineMs !== undefined && Date.now() >= options.deadlineMs) throw new ScheduleGenerationTimeoutError();
}

/** The real task boundary lets input/paint and deadline/abort delivery run between CPU slices. */
function yieldRun(options: CooperativeGenerationOptions): Promise<void> {
  checkRun(options);
  return new Promise((resolve, reject) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let taskTimer: ReturnType<typeof setTimeout> | undefined;
    const finish = (failed: boolean, error?: unknown) => {
      if (settled) return;
      settled = true;
      if (timer !== undefined) clearTimeout(timer);
      if (taskTimer !== undefined) clearTimeout(taskTimer);
      options.signal?.removeEventListener('abort', abort);
      if (failed) reject(error); else resolve();
    };
    const abort = () => {
      try { checkRun(options); } catch (error) { finish(true, error); }
    };
    options.signal?.addEventListener('abort', abort, { once: true });
    if (options.deadlineMs !== undefined) {
      const remaining = options.deadlineMs - Date.now();
      if (remaining <= 0) { finish(true, new ScheduleGenerationTimeoutError()); return; }
      timer = setTimeout(() => finish(true, new ScheduleGenerationTimeoutError()), Math.min(remaining, 2_147_483_647));
    }
    try {
      const task = options.yieldTask ? options.yieldTask() : new Promise<void>(done => { taskTimer = setTimeout(done, 0); });
      task.then(() => finish(false), error => finish(true, error));
    } catch (error) { finish(true, error); }
    if (options.signal?.aborted) abort();
  });
}

/** One captured term snapshot and one shared iterator cover preparation, search, scoring and diagnosis. */
export async function generateScheduleOptionsCooperatively(input: CooperativeGenerationInput, options: CooperativeGenerationOptions = {}): Promise<CooperativeGenerationResult> {
  checkRun(options);
  // Capture before the first task yield. Cache replacement/mutation cannot alter a running search.
  const open = new Set(openSectionsService.openSectionsCache?.get(input.openSectionsTermId) ?? []);
  const steps = optionsSteps(input, index => open.has(index.split('-')[0]));
  const maximum = Number.isFinite(options.maxStepsPerSlice) ? Math.max(1, Math.floor(options.maxStepsPerSlice!)) : 128;
  const sliceMs = Number.isFinite(options.maxSliceMs) ? Math.max(1, options.maxSliceMs!) : 8;
  try {
    await yieldRun(options);
    while (true) {
      const started = Date.now();
      let count = 0;
      do {
        checkRun(options);
        const next = steps.next();
        if (next.done) { checkRun(options); return next.value; }
        count++;
      } while (count < maximum && Date.now() - started < sliceMs);
      await yieldRun(options);
    }
  } finally {
    steps.return(undefined as never);
  }
}

export interface ScheduleScoreBreakdown {
  total: number;
  campusScore: number;
  professorScore: number;
  timeScore: number;
  logisticsPenalty: number;
  friendBonus: number;
  weightedCampus: number;
  weightedProfessor: number;
  weightedTime: number;
  baseScore: number;
  scoreBeforeClamp: number;
  weights: {
    campus: number;
    professor: number;
    times: number;
  };
}

function buildPriorityWeights(preferences: Preferences): ScheduleScoreBreakdown['weights'] {
  const weights = new Map<string, number>();
  const totalPriority = (preferences.priorities || []).reduce((s, p) => s + (p.value || 0), 0) || 100;
  (preferences.priorities || [
    { name: 'campus', value: 33, color: '' },
    { name: 'professor ratings', value: 33, color: '' },
    { name: 'times', value: 34, color: '' }
  ]).forEach(p => weights.set(p.name.toLowerCase(), (p.value || 0) / totalPriority));

  return {
    campus: weights.get('campus') ?? 0.34,
    professor: weights.get('professor ratings') ?? 0.33,
    times: weights.get('times') ?? 0.33,
  };
}

function computeProfessorScore(schedule: ScheduledCourse[], preferences: Preferences): number {
  if (schedule.length === 0) return 0;

  const avg = schedule.reduce((sum, c) => {
    const ratings = c.instructors.map(i => (typeof i !== 'string' && i.rating) ? i.rating : 3.0);
    const courseAvg = ratings.reduce((a, b) => a + b, 0) / (ratings.length || 1);
    return sum + (courseAvg / 5) * 100;
  }, 0) / schedule.length;

  const min = preferences.minimumProfessorRating || 0;
  const floor = (min / 5) * 80;
  return Math.max(avg - (floor > 0 ? (80 - floor) / 2 : 0), 0);
}

function computeLogisticsPenalty(schedule: ScheduledCourse[], preferences: Preferences): number {
  if (preferences.disableLogisticsPenalties) return 0;

  let logisticsPenalty = 0;
  const byDay: { [day: string]: ScheduledCourse[] } = {};
  schedule.forEach(c => c.meetingTimes.forEach(mt => {
    if (!byDay[mt.day]) byDay[mt.day] = [];
    byDay[mt.day].push({ ...c, meetingTimes: [{ ...mt }] } as ScheduledCourse);
  }));

  for (const day of Object.keys(byDay)) {
    const items = byDay[day]
      .map(c => ({ c, start: c.meetingTimes[0].startMinute, end: c.meetingTimes[0].endMinute }))
      .sort((a, b) => a.start - b.start);
    for (let i = 0; i < items.length - 1; i++) {
      const a = items[i];
      const b = items[i + 1];
      const travel = TRAVEL_TIMES[a.c.campus]?.[b.c.campus] ?? 20;
      const gap = b.start - a.end;
      if (gap < travel) logisticsPenalty += 8;
      if (gap > 120) logisticsPenalty += 1;
    }
  }

  return logisticsPenalty;
}

function computeFriendBonus(schedule: ScheduledCourse[], friendSchedules?: ScheduledCourse[][]): number {
  if (!friendSchedules || friendSchedules.length === 0) return 0;

  let friendBonus = 0;
  const scheduleSectionIds = new Set(schedule.map(course => course.id));
  for (const friendSchedule of friendSchedules) {
    for (const friendCourse of friendSchedule) {
      if (scheduleSectionIds.has(friendCourse.id)) {
        friendBonus += 10;
      }
    }
  }
  return friendBonus;
}

export function getScheduleScoreBreakdown(
  schedule: ScheduledCourse[],
  preferences: Preferences,
  friendSchedules?: ScheduledCourse[][]
): ScheduleScoreBreakdown {
  const weights = buildPriorityWeights(preferences);
  const professorScore = computeProfessorScore(schedule, preferences);
  const timeScore = computeTimeScore(schedule, preferences);
  const campusScore = computeCampusScore(schedule, preferences);
  const logisticsPenalty = computeLogisticsPenalty(schedule, preferences);
  const friendBonus = computeFriendBonus(schedule, friendSchedules);

  const weightedCampus = campusScore * weights.campus;
  const weightedProfessor = professorScore * weights.professor;
  const weightedTime = timeScore * weights.times;
  const baseScore = weightedCampus + weightedProfessor + weightedTime;
  const scoreBeforeClamp = baseScore - Math.min(25, logisticsPenalty) + friendBonus;

  return {
    total: Math.round(Math.max(0, Math.min(100, scoreBeforeClamp))),
    campusScore,
    professorScore,
    timeScore,
    logisticsPenalty,
    friendBonus,
    weightedCampus,
    weightedProfessor,
    weightedTime,
    baseScore,
    scoreBeforeClamp,
    weights,
  };
}

// --- Schedule Scoring ---
export function scoreSchedule(
  schedule: ScheduledCourse[],
  preferences: Preferences,
  friendSchedules?: ScheduledCourse[][]
): number {
  return getScheduleScoreBreakdown(schedule, preferences, friendSchedules).total;
}

// Exported helper: compute the time preference subscore (0..100) from availability grid and time window only
export function computeTimeScore(
  schedule: ScheduledCourse[],
  preferences: Preferences
): number {
  let timePenalty = 0;
  // Penalize time outside preferred window and unavailable grid; ignore online/asynchronous and TBA
  for (const c of schedule) {
    if (isOnlineAsync(c)) continue;
    if (isTBA(c)) continue;
    for (const mt of c.meetingTimes) {
      // Skip invalid meeting times (TBA)
      if (!mt.startMinute || !mt.endMinute || mt.startMinute <= 0 || mt.endMinute <= 0) continue;

      const dayIndex = AVAILABILITY_DAYS.indexOf(mt.day as typeof AVAILABILITY_DAYS[number]);
      if (dayIndex !== -1) {
        for (let m = mt.startMinute; m < mt.endMinute; m += 60) {
          const hourIdx = getAvailabilitySlotIndex(m);
          if (hourIdx >= 0 && hourIdx < AVAILABILITY_SLOT_COUNT) {
            const unavailable = !preferences.availability?.[dayIndex]?.[hourIdx];
            if (unavailable) timePenalty += 1;
          }
        }
      }
      if (mt.startMinute < preferences.earliestStartTime) timePenalty += 4;
      if (mt.endMinute > preferences.latestEndTime) timePenalty += 4;
    }
  }
  return Math.max(0, 100 - Math.min(50, timePenalty));
}

// Exported helper: compute campus preference subscore (0..100) from preferences only
export function computeCampusScore(
  schedule: ScheduledCourse[],
  preferences: Preferences
): number {
  const entries = (preferences.campusPreferences || []).map(cp => ({
    key: normalizeCampusName(cp.name),
    value: cp.value
  }));
  const maxValue = Math.max(1, ...entries.map(e => e.value));
  // Map each campus to a 0..100 preference score where the most-preferred campus = 100
  const campusMap = new Map<string, number>();
  entries.forEach(e => campusMap.set(e.key, Math.round((e.value / maxValue) * 100)));
  if (schedule.length === 0) return 100;
  const total = schedule.reduce((sum, c) => {
    const key = normalizeCampusName(c.campus);
    const v = campusMap.has(key) ? campusMap.get(key)! : 50;
    return sum + v;
  }, 0);
  return total / schedule.length;
}
